// Theme preference: follow the OS ("system") or force light/dark. The CSS in
// styles/theme.css does the work; this only sets <html data-theme> and
// remembers the viewer's choice. Storage can be unavailable (private mode,
// blocked site data), so every access is guarded and the default is "system".

export type ThemePreference = 'system' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'sss.theme';

const ORDER: readonly ThemePreference[] = ['system', 'light', 'dark'];

export const isThemePreference = (value: unknown): value is ThemePreference =>
  typeof value === 'string' && (ORDER as readonly string[]).includes(value);

/** The preference after `current` when the toggle is pressed. */
export const nextThemePreference = (current: ThemePreference): ThemePreference =>
  ORDER[(ORDER.indexOf(current) + 1) % ORDER.length] as ThemePreference;

export const readThemePreference = (storage: Pick<Storage, 'getItem'> | undefined): ThemePreference => {
  try {
    const stored = storage?.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
};

export const storeThemePreference = (storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined, pref: ThemePreference): void => {
  try {
    if (pref === 'system') storage?.removeItem(THEME_STORAGE_KEY);
    else storage?.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // Not persisted; the choice still applies for this page view.
  }
};

/** Apply to the document: no attribute for "system" so the CSS media query decides. */
export const applyThemePreference = (root: HTMLElement, pref: ThemePreference): void => {
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
};

const safeLocalStorage = (): Storage | undefined => {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

export const browserThemeStorage = safeLocalStorage;
